import React, { ReactNode } from 'react';
import styled from 'styled-components';
// eslint-disable-next-line import/no-namespace
import * as ss from 'styled-system';
import {
  BorderProps,
  ColorProps,
  DisplayProps,
  FlexboxProps,
  GridProps,
  HeightProps,
  PositionProps,
  ResponsiveValue,
  ShadowProps,
  SpaceProps,
  TypographyProps,
  WidthProps,
} from 'styled-system';

import { getClassName } from 'theme';

type ComponentProps = {
  className?: string;
};

type AllStyledSystemProps = FlexboxProps &
  SpaceProps &
  TypographyProps &
  BorderProps &
  DisplayProps &
  PositionProps &
  ColorProps &
  WidthProps &
  HeightProps &
  ShadowProps &
  GridProps;

type AdditionalStyleProps = {
  transform: ResponsiveValue<string>;
  whiteSpace: ResponsiveValue<string>;
  wordBreak: ResponsiveValue<string>;
  transition: ResponsiveValue<string>;
  willChange: ResponsiveValue<string>;
  pointerEvents: ResponsiveValue<string>;
};

type BoxProps = AllStyledSystemProps &
  Partial<AdditionalStyleProps> & {
    children?: ReactNode;
    classes?: Array<UtilityClass>;
    component?: React.ComponentType | React.ElementType;
  } & Record<string, unknown>;

const BaseBox = styled(
  ({
    dispatch,
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    transform,
    m,
    mr,
    ml,
    mt,
    mb,
    mx,
    my,
    p,
    pr,
    pl,
    pt,
    pb,
    px,
    py,
    display,
    width,
    height,
    maxWidth,
    maxHeight,
    minWidth,
    minHeight,
    flex,
    order,
    alignSelf,
    justifySelf,
    alignItems,
    alignContent,
    justifyContent,
    flexBasis,
    flexDirection,
    flexGrow,
    flexWrap,
    gridGap,
    gridAutoFlow,
    gridArea,
    gridTemplateAreas,
    gridColumn,
    gridColumnGap,
    gridAutoColumns,
    gridTemplateColumns,
    gridRow,
    gridRowGap,
    gridAutoRows,
    gridTemplateRows,
    columnStart,
    columnEnd,
    overflow,
    color,
    opacity,
    visibility,
    bg,
    backgroundImage,
    backgroundSize,
    backgroundPosition,
    backgroundRepeat,
    backgroundColor,
    borderBottom,
    borderTop,
    borderLeft,
    borderRight,
    border,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    boxShadow,
    fontSize,
    fontFamily,
    lineHeight,
    fontWeight,
    letterSpacing,
    textAlign,
    verticalAlign,
    textOverflow,
    textTransform,
    textDecoration,
    whiteSpace,
    wordBreak,
    transition,
    willChange,
    pointerEvents,
    component,
    classes,
    className,
    printHidden,
    ...props
  }: {
    component?: React.ComponentType<ComponentProps>;
    classes?: Array<UtilityClass>;
    className?: string;
    printHidden?: boolean;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch: (...args: Array<any>) => any;
    position: string;
    top: string;
    right: string;
    bottom: string;
    left: string;
    zIndex: string;
    transform: string;
    m: string;
    mr: string;
    ml: string;
    mt: string;
    mb: string;
    mx: string;
    my: string;
    p: string;
    pr: string;
    pl: string;
    pt: string;
    pb: string;
    px: string;
    py: string;
    display: string;
    width: string;
    height: string;
    maxWidth: string;
    maxHeight: string;
    minWidth: string;
    minHeight: string;
    flex: string;
    order: string;
    alignSelf: string;
    justifySelf: string;
    alignItems: string;
    alignContent: string;
    justifyContent: string;
    flexBasis: string;
    flexDirection: string;
    flexGrow: string;
    flexWrap: string;
    columnStart: string;
    columnEnd: string;
    overflow: string;
    color: string;
    opacity: string;
    visibility: string;
    bg: string;
    backgroundImage: string;
    backgroundSize: string;
    backgroundPosition: string;
    backgroundRepeat: string;
    backgroundColor: string;
    borderBottom: string;
    borderTop: string;
    borderLeft: string;
    borderRight: string;
    border: string;
    borderColor: string;
    borderWidth: string;
    borderStyle: string;
    borderRadius: string;
    boxShadow: string;
    fontSize: string;
    fontFamily: string;
    gridGap: string;
    gridAutoFlow: string;
    gridArea: string;
    gridTemplateAreas: string;
    gridColumn: string;
    gridColumnGap: string;
    gridAutoColumns: string;
    gridTemplateColumns: string;
    gridRow: string;
    gridRowGap: string;
    gridAutoRows: string;
    gridTemplateRows: string;
    lineHeight: string;
    fontWeight: string;
    letterSpacing: string;
    textAlign: string;
    verticalAlign: string;
    textOverflow: string;
    textTransform: string;
    textDecoration: string;
    whiteSpace: string;
    wordBreak: string;
    transition: string;
    willChange: string;
    pointerEvents: string;
  }) => {
    const BoxComponent = component || 'div';
    return (
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/jsx-props-no-spreading
      <BoxComponent {...props} className={getClassName(className, classes)} />
    );
  }
).attrs(
  ({
    transition,
    zIndex,
    justifySelf,
    alignSelf,
    alignContent,
    // @ts-expect-error Fix types
    filter,
    opacity,
    // @ts-expect-error Fix types
    background,
    backgroundImage,
    backgroundRepeat,
    backgroundColor,
    backgroundPosition,
    backgroundSize,
    textOverflow,
    textTransform,
    textDecoration,
    whiteSpace,
    willChange,
    wordBreak,
    visibility,
    verticalAlign,
    pointerEvents,
    letterSpacing,
    lineHeight,
  }) => ({
    style: {
      transition,
      zIndex,
      justifySelf,
      alignSelf,
      alignContent,
      filter,
      opacity,
      background,
      backgroundImage,
      backgroundRepeat,
      backgroundColor,
      backgroundPosition,
      backgroundSize,
      textOverflow,
      textTransform,
      textDecoration,
      whiteSpace,
      willChange,
      wordBreak,
      visibility,
      verticalAlign,
      pointerEvents,
      letterSpacing,
      lineHeight,
    },
  })
)`
  ${ss.position}
  ${ss.top}
  ${ss.right}
  ${ss.bottom}
  ${ss.left}
  ${ss.space}
  ${ss.display}
  ${ss.width}
  ${ss.height}
  ${ss.maxWidth}
  ${ss.maxHeight}
  ${ss.minWidth}
  ${ss.minHeight}
  ${ss.flex}
  ${ss.order}
  ${ss.alignItems}
  ${ss.justifyContent}
  ${ss.flexBasis}
  ${ss.flexWrap}
  ${ss.flexDirection}
  ${ss.flexGrow}
  ${ss.gridGap}
  ${ss.gridAutoFlow}
  ${ss.gridArea}
  ${ss.gridTemplateAreas}
  ${ss.gridColumn}
  ${ss.gridColumnGap}
  ${ss.gridAutoColumns}
  ${ss.gridTemplateColumns}
  ${ss.gridRow}
  ${ss.gridRowGap}
  ${ss.gridAutoRows}
  ${ss.gridTemplateRows}
  ${ss.color}
  ${ss.borders}
  ${ss.borderRadius}
  ${ss.boxShadow}
  ${ss.fontSize}
  ${ss.fontWeight}
  ${ss.textAlign}
  ${ss.system({
    transform: true,
    overflow: true,
  })}
  ${(props) =>
    props.printHidden &&
    `
    @media print {
      display: none !important;
    }
  `}
`;

export function Box({ classes, ...props }: BoxProps) {
  // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/no-explicit-any
  return <BaseBox classes={classes} {...(props as any)} />;
}
